import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FeeScheduleCategory from "../fee-schedule-category";
import { useMemo } from "react";
import { MRTNoMemo } from "_shared/_views/tables/table";

const RowTitle = styled(Typography)(() => ({
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));

type FeeScheduleCategoriesReadonlyProps = {
  className?: string;
  categories: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  commentThreads?: CommentThread[];
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  hideCommentButton?: boolean;
};

const columnHelper = createMRTColumnHelper<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>();

export default function ReadonlyFeeScheduleCategoriesTable(props: Readonly<FeeScheduleCategoriesReadonlyProps>) {
  const { className, categories, commentThreads, onCommentsClicked, hideCommentButton } = props;

  const session = useSession();

  const columns = useMemo(() => [
    columnHelper.accessor((category) => <RowTitle>{category.name.value}</RowTitle>, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor((category) => category.description?.value, {
      header: "Description",
      id: "description",
    }),
    columnHelper.accessor(
      (category) => {
        let fixedValue;
        if (category instanceof FeeScheduleCategoryMaskedCurrency) {
          fixedValue = "Masked";
        } else if (category?.fee?.unit) {
          fixedValue = "No";
        } else {
          fixedValue = "Yes";
        }
        return fixedValue;
      },
      {
        header: "Fixed?",
        id: "fixedFee",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (category) => {
        const deferredTo = session.context?.viewingAsVendor === true ? "client" : "vendor";
        let feeValue;
        if (category instanceof FeeScheduleCategoryMaskedCurrency) {
          feeValue = "Masked";
        } else if (category.fee) {
          feeValue = new Intl.NumberFormat(navigator.language, {
            style: "currency",
            currency: category.fee?.rate?.currency.toString() ?? "USD",
          }).format(category.fee?.rate?.amount ?? 0);
        } else {
          feeValue = <Typography color="warning">{`Deferred to ${deferredTo}`}</Typography>;
        }
        return feeValue;
      },
      {
        header: "Fee",
        id: "fee",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (category) =>
        category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.fee?.unit ?? <Icon>remove</Icon>,
      {
        header: "Per",
        id: "unit",
        size: 50,
      }
    ),
    columnHelper.accessor(
      (category) =>
        category.billingCode?.value && category.billingCode.value.length > 0 ? (
          category.billingCode.value
        ) : (
          <Icon>remove</Icon>
        ),
      {
        header: "Code",
        id: "billingCode",
        size: 50,
      }
    ),
  ], [session.context?.viewingAsVendor]);
  
  const tableOptions: MRT_TableOptions<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> = {
    columns,
    data: categories,
    enableRowSelection: false,
    enableTableHead: true,
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      showColumnFilters: false,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    enableColumnPinning: true,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    enableRowActions: !hideCommentButton,
    muiTableBodyRowProps: () => ({
      sx: {
        pointerEvents: "none",
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <ToggleGlobalFilterButton table={table} />
        <ToggleFiltersButton table={table} />
        <ShowHideColumnsButton table={table} />
        <ToggleDensePaddingButton table={table} />
        <ToggleFullScreenButton table={table} />
        {!hideCommentButton && (
          <CommentsButton
            commentThreads={commentThreads}
            hideUnreadCount={true}
            field={ProposalField.FeeSchedule}
            toolTip={
              commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.FeeSchedule))
                ? "Comments"
                : "No Comments"
            }
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                    onCommentsClicked(ProposalField.FeeSchedule);
                  }
                : undefined
            }
          />
        )}
      </Box>
    ),
    renderRowActions: ({ row }) => {
      return (
        <RowActionsContainer>
          <CommentsButton
            field={ProposalField.FeeScheduleCategory(row.original.id)}
            hideUnreadCount={true}
            commentThreads={commentThreads}
            toolTip={
              commentThreads?.some((thread) =>
                thread.field.isEqualTo(ProposalField.FeeScheduleCategory(row.original.id))
              )
                ? "Comments"
                : "No Comments"
            }
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                    onCommentsClicked(ProposalField.FeeScheduleCategory(row.original.id));
                  }
                : undefined
            }
          />
        </RowActionsContainer>
      );
    },
    muiTableContainerProps: { sx: { flex: 1 } },
    muiTablePaperProps: { elevation: 0, sx: { height: "100%", display: "flex", flexDirection: "column" } },
  }

  return (
    <div className={className}>
      <MRTNoMemo<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> options={tableOptions} />
    </div>
  );
}
